<script lang="ts">
	import { getColorForString } from '../../helper/Colors';
	import { onDestroy, onMount } from 'svelte';
	import { clickOutside } from '../../helper/Extensions';
	import Icon from '../Icon.svelte';
	import type { MultiSelectionItem, SimpleListStore, StoreListener } from '../../types/ListTypes';

	interface Props {
		id?: string | null;
		hint?: string;
		store: SimpleListStore;
		displayPropertyName?: string;
		onOptionSelected: (item: any) => void;
	}

	let {
		id = null,
		hint = '',
		store,
		displayPropertyName = 'name',
		onOptionSelected
	}: Props = $props();

	let scrollListener: HTMLElement | undefined = $state();
	let scrollContainer: HTMLElement | undefined = $state();
	let observer: IntersectionObserver;
	let loading: boolean = $state(false);
	let loopCount: number = $state(0);

	let showDropdown = $state(false);
	let selectedItems: MultiSelectionItem[] = $state([]);

	let storeListener: StoreListener = {
		onFilterApplied: () => {
			//If the scroll listener is not yet bound do not remove the observer
			if (scrollListener) {
				//We have to remove the listener otherwise the event may not be send again
				if (observer) {
					observer.disconnect();
					//Add it back again
					observer.observe(scrollListener);
				}
			}
		}
	};

	//Reactive prop for showing selected items
	$effect(() => {
		if (store.data) {
			selectedItems = store.data.filter((item) => item.selected);
		}
	});

	function handleSelect(item: MultiSelectionItem) {
		//Mark the item
		item.selected = !item.selected;

		selectedItems = store.data.filter((item) => item.selected);

		onOptionSelected(item);
	}

	function handleKeyDown(event: KeyboardEvent) {
		let handeld = false;

		switch (event.key) {
			case 'Enter': {
				handeld = true;
				showDropdown = !showDropdown;
				break;
			}
			case 'ESC':
			case 'Escape': {
				handeld = true;
				showDropdown = false;
				break;
			}
		}

		if (handeld) {
			event.stopPropagation();
			event.preventDefault();
		}
	}

	async function requestLoadMore(element: HTMLElement) {
		if (loading) return;
		loading = true;

		const total = store.data.length + store.listFilter.deletedCounter;
		const size = store.listFilter.size || 0;
		const pageSizeOk = total % size === 0;
		const nextPagePossible = store.listFilter.page * size <= total;

		//Check if we should actually load more.
		if (!pageSizeOk || !nextPagePossible) {
			loading = false;
			return;
		}

		//Make the effective load call
		await store.loadData();

		loading = false;

		// Check if marker is still visible (screen too big)
		if (scrollContainer && element.getBoundingClientRect().top < scrollContainer.getBoundingClientRect().bottom) {

			//Simple loop protection
			if (loopCount < 7) {
				loopCount += 1;
				//If it is try to load more
				await requestLoadMore(element);
				//Reset the loop count after
				loopCount = 0;
			}
		}
	}

	function setupObserver(element: HTMLElement) {
		observer = new IntersectionObserver(
			(entries) => {
				if (entries[0].isIntersecting) {
					requestLoadMore(element);
				}
			},
			{
				root: scrollContainer,
				rootMargin: '20px',
				threshold: 0
			}
		);

		observer.observe(element);
	}

	onMount(() => {
		store.addNotifier(storeListener);
	});

	onDestroy(() => {
		//Remove the listener again
		store.removeNotifier(storeListener);

		if (observer) {
			observer.disconnect();
		}
	});
</script>

<!-- svelte-ignore a11y_no_noninteractive_tabindex -->
<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
	{id}
	class="dropdown outline-none"
	tabindex="0"
	use:clickOutside={() => {
		showDropdown = false;
	}}
	onkeydown={handleKeyDown}
>
	<div class="relative outline-none w-full h-full normal-style">
		<button
			class="flex p-2 gap-2 items-center w-full"
			onclick={() => {
				showDropdown = !showDropdown;
			}}
		>
			<!--Option container-->
			{#if selectedItems.length === 0}
				<p class="grow">{hint}</p>
			{:else}
				<div class="flex gap-2 items-start grow flex-wrap">
					{#each selectedItems as item}
						<div
							style="background-color: {getColorForString(item.id.toString())};"
							class="rounded px-2 py-1 text-white"
						>
							<p class="text-sm pointer-events-none">
								{item[displayPropertyName] || ''}
							</p>
						</div>
					{/each}
				</div>
			{/if}

			<Icon name="chevron-down" size="small" classInfo="dark:fill-white" />
		</button>

		{#if showDropdown}
			<div
				bind:this={scrollContainer}
				style="max-height: 250px; min-width: 100%;"
				class="absolute bg-white dark:bg-dark-400 outline-none border border-gray rounded shadow-xl z-40 grid overflow-y-auto"
			>
				{#each store.data as item}
					<button
						class="option b-1 p-2 flex"
						onclick={(_) => {
							handleSelect(item);
						}}
					>
						<div class="flex gap-2 items-center">
							<div
								class="w-4 h-4 rounded border border-gray selected:bg-secondary-200 dark:selected:bg-secondary-500"
								class:selected={item.selected}
							></div>
							<p>{item[displayPropertyName] || ''}</p>
						</div>
					</button>
				{/each}

				{#if store.listFilter.size || 0 > 0}
					<div bind:this={scrollListener} class="h-2" use:setupObserver></div>
				{/if}
			</div>
		{/if}
	</div>
</div>

<style>
	.normal-style :global {
		border-radius: 0.25rem;
		border-width: 2px;
		--tw-border-opacity: 1;
		border-color: rgb(227 227 227 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(253 253 253 / var(--tw-bg-opacity, 1));
}

.normal-style :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(56 56 56 / var(--tw-border-opacity, 1));
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
}

	.dropdown:focus :global {
		border-color: #2ddaff;
	}

	.dropdown:hover :global {
		border-color: #2ddaff;
	}

	.option :global {
		border-bottom-width: 1px;
		--tw-border-opacity: 1;
		border-color: rgb(227 227 227 / var(--tw-border-opacity, 1));
}

	.option :global:where(.dark, .dark *) {
		--tw-border-opacity: 1;
		border-color: rgb(78 78 78 / var(--tw-border-opacity, 1));
}

	.option:hover :global {
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.option:hover :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
}

	.option:focus :global {
		--tw-bg-opacity: 1;
		background-color: rgb(23 137 192 / var(--tw-bg-opacity, 1));
		--tw-text-opacity: 1;
		color: rgb(253 253 253 / var(--tw-text-opacity, 1));
}

	.option:focus :global:where(.dark, .dark *) {
		--tw-bg-opacity: 1;
		background-color: rgb(56 56 56 / var(--tw-bg-opacity, 1));
}
</style>
